'use client';

import { useEffect, useMemo, useState } from 'react';
import { Carousel } from '@mantine/carousel';
import { useParams } from 'next/navigation';
import classes from './NeumoReviews.module.scss';
import { useDictionary } from '@/src/dictionaries/dictionary-provider';
import { IconSparkles, IconStarFilled } from '@tabler/icons-react';
import '@mantine/carousel/styles.css';

const FEATURABLE_ID = process.env.NEXT_PUBLIC_FEATURABLE_ID as string | undefined;

// ===============================
// MOCK REVIEWS (NO COOKIES)
// ===============================
const MOCK_REVIEWS: NormalizedReview[] = [
  {
    id: 'mock-1',
    authorName: 'Joseph Fugier',
    avatarUrl: null,
    ratingValue: 5,
    ratingMax: 5,
    text: 'Entièrement satisfait de cette entreprise, je recommande.',
    originalText: 'Entièrement satisfait de cette entreprise, je recommande.',
    sourceLabel: 'Avis Google',
  },
  {
    id: 'mock-2',
    authorName: 'Pauline B.',
    avatarUrl: null,
    ratingValue: 5,
    ratingMax: 5,
    text: 'Très professionnel et à l’écoute de ses clients. Je recommande vivement! Vivien nous a accompagné tout au long de notre projet avec beaucoup de patience et de pédagogie. Il a mis en place un site web à notre image, moderne et fonctionnel et a ajouté des automatismes qui nous font gagner un temps précieux au quotidien. Un grand merci à lui et à toute l’équipe!',
    originalText: 'Très professionnel et à l’écoute de ses clients. Je recommande vivement! Vivien nous a accompagné tout au long de notre projet avec beaucoup de patience et de pédagogie. Il a mis en place un site web à notre image, moderne et fonctionnel et a ajouté des automatismes qui nous font gagner un temps précieux au quotidien. Un grand merci à lui et à toute l’équipe!',
    sourceLabel: 'Avis Google',
  }
];


type ApiReviewV1 = {
  reviewId: string;
  reviewer: {
    displayName: string;
    profilePhotoUrl?: string | null;
  };
  starRating: number;
  comment: string;
  createTime?: string;
  updateTime?: string;
  reviewReply?: {
    comment: string;
    updateTime?: string;
  };
};

type ApiResponseV1 = {
  success: boolean;
  widget: string; // UUID
  totalReviewCount?: number;
  averageRating?: number;
  profileUrl?: string;
  reviews?: ApiReviewV1[];
};

type LegacyReview = {
  id: string;
  platform: string;
  author: {
    name: string;
    avatarUrl: string;
    profileUrl: string | null;
  };
  title: string | null;
  text: string;
  originalText: string;
  languageCode: string;
  rating: { value: number; max: number };
  publishedAt: string;
  updatedAt: string;
  createdAt: string;
  lastSyncedAt: string;
  metadata: Record<string, unknown>;
  url: string | null;
};

type LegacyResponse = {
  success: boolean;
  widget?: {
    uuid: string;
    config: Record<string, unknown>;
    reviews: LegacyReview[];
    isExampleReviews: boolean;
    gbpLocationUuid: string;
    gbpLocationSummary?: {
      reviewsCount: number;
      rating: number;
      writeAReviewUri?: string;
    };
    showBranding: boolean;
  };
};

type NormalizedReview = {
  id: string;
  authorName: string;
  avatarUrl?: string | null;
  ratingValue: number; // 0-5
  ratingMax: number; // usually 5
  text: string; // translated or default
  originalText: string; // original if available
  sourceLabel: string; // "Google"
};

type NeumoReviewsProps = {
  badge?: string;
  title?: string;
  description?: string;
};

function renderStars(value?: number) {
  const rounded = Math.round(value ?? 0);
  return (
    <div className={classes.starRow} aria-hidden="true">
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i} className={i <= rounded ? classes.starActive : classes.starInactive}>
          <IconStarFilled size={20} />
        </span>
      ))}
    </div>
  );
}

function getInitial(name?: string) {
  const trimmed = name?.trim();
  if (!trimmed) return '?';
  return trimmed.charAt(0).toUpperCase();
}

/**
 * Featurable (your V1) comment often looks like:
 * "(Translated by Google) ...\n\n(Original)\nFrench text"
 *
 * We extract both versions when possible.
 */
function parseTranslatedAndOriginal(comment: string) {
  const text = comment ?? '';
  const originalMarker = /\(Original\)\s*/i;
  const translatedMarker = /\(Translated by Google\)\s*/i;

  // Split on "(Original)"
  const parts = text.split(originalMarker);

  let translatedPart = text;
  let originalPart = '';

  if (parts.length >= 2) {
    translatedPart = parts[0];
    originalPart = parts.slice(1).join('(Original)\n'); // in case multiple markers
  }

  translatedPart = translatedPart.replace(translatedMarker, '').trim();
  originalPart = originalPart.trim();

  return { translated: translatedPart, original: originalPart };
}

function computeAverage(reviews: NormalizedReview[]) {
  if (!reviews.length) return undefined;
  const sum = reviews.reduce((acc, r) => acc + (r.ratingValue || 0), 0);
  return sum / reviews.length;
}

export const NeumoReviews = ({ data }: { data: NeumoReviewsProps }) => {
  const { badge, title, description } = data;

  const dictionary = useDictionary();
  const reviewsTexts = dictionary.reviews ?? {};

  const params = useParams();
  const currentLang = (params?.lang as string | undefined)?.toLowerCase() ?? 'fr';
  const isFrench = currentLang.startsWith('fr');
  const isEnglish = currentLang.startsWith('en');

  const [reviews, setReviews] = useState<NormalizedReview[]>([]);
  const [averageRating, setAverageRating] = useState<number | undefined>();
  const [totalReviewCount, setTotalReviewCount] = useState<number | undefined>();
  const [profileUrl, setProfileUrl] = useState<string | undefined>();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [embla, setEmbla] = useState<any | null>(null);

  const displayTitle =
    title || (reviewsTexts.title as string) || 'Ce que disent nos clients';

  const displayDescription =
    description ||
    (reviewsTexts.subtitle as string) ||
    'Avis vérifiés directement depuis notre fiche Google Business.';

  const formatAverageLabel = (count: number) =>
    (reviewsTexts.averageRatingLabel as string | undefined)?.replace('{{count}}', String(count)) ??
    `Note moyenne sur ${count} avis`;

  const formatSrRating = (rating: number, max: number) =>
    (reviewsTexts.srRating as string | undefined)
      ?.replace('{{rating}}', String(rating))
      ?.replace('{{max}}', String(max)) ?? `Note ${rating} sur ${max}`;

  // pick the right display text based on locale (prefer original on FR)
  const getReviewText = (r: NormalizedReview) => {
    if (isFrench) return (r.originalText || r.text || '').trim();
    if (isEnglish) return (r.text || r.originalText || '').trim();
    return (r.text || r.originalText || '').trim();
  };

  // useEffect(() => {
  //   if (!FEATURABLE_ID) {
  //     console.error('Featurable widget ID is not configured (NEXT_PUBLIC_FEATURABLE_ID).');
  //     setIsLoading(false);
  //     return;
  //   }

  //   const fetchReviews = async () => {
  //     try {
  //       setIsLoading(true);
  //       setError(null);

  //       const res = await fetch(`https://featurable.com/api/v1/widgets/${FEATURABLE_ID}`, {
  //         // avoid cached stale reviews in production if desired:
  //         // cache: 'no-store',
  //       });

  //       if (!res.ok) throw new Error(`HTTP ${res.status}`);

  //       const raw = (await res.json()) as ApiResponseV1 | LegacyResponse;
  //       // console.log('Featurable response', raw);

  //       if (!raw || (raw as any).success !== true) {
  //         throw new Error('Featurable API returned success = false');
  //       }

  //       // ---- normalize both possible shapes ----
  //       // Shape A (your V1): raw.reviews at root
  //       const v1Reviews = (raw as ApiResponseV1).reviews;
  //       const legacyWidget = (raw as LegacyResponse).widget;

  //       let normalized: NormalizedReview[] = [];

  //       if (Array.isArray(v1Reviews)) {
  //         normalized = v1Reviews.map((r) => {
  //           const { translated, original } = parseTranslatedAndOriginal(r.comment || '');
  //           return {
  //             id: r.reviewId,
  //             authorName: r.reviewer?.displayName ?? 'Google user',
  //             avatarUrl: r.reviewer?.profilePhotoUrl ?? null,
  //             ratingValue: Number(r.starRating ?? 0),
  //             ratingMax: 5,
  //             text: translated || original || '',
  //             originalText: original || '',
  //             sourceLabel: (reviewsTexts.sourceLabel as string) ?? 'Avis Google',
  //           };
  //         });
  //         setProfileUrl((raw as ApiResponseV1).profileUrl);
  //         setAverageRating((raw as ApiResponseV1).averageRating);
  //         setTotalReviewCount((raw as ApiResponseV1).totalReviewCount);
  //       } else if (legacyWidget && Array.isArray(legacyWidget.reviews)) {
  //         normalized = legacyWidget.reviews.map((r) => ({
  //           id: r.id,
  //           authorName: r.author?.name ?? 'Google user',
  //           avatarUrl: r.author?.avatarUrl ?? null,
  //           ratingValue: r.rating?.value ?? 0,
  //           ratingMax: r.rating?.max ?? 5,
  //           text: r.text ?? '',
  //           originalText: r.originalText ?? '',
  //           sourceLabel: (reviewsTexts.sourceLabel as string) ?? 'Avis Google',
  //         }));
  //         setProfileUrl(legacyWidget.gbpLocationSummary?.writeAReviewUri);
  //         setAverageRating(legacyWidget.gbpLocationSummary?.rating);
  //         setTotalReviewCount(legacyWidget.gbpLocationSummary?.reviewsCount);
  //       }

  //       // Remove empty reviews
  //       normalized = normalized.filter((r) => (r.text || r.originalText).trim().length > 0);

  //       // If EN: prefer those that actually have translated text; otherwise keep all
  //       if (isEnglish) {
  //         const withEnglishText = normalized.filter((r) => (r.text || '').trim().length > 0);
  //         if (withEnglishText.length) normalized = withEnglishText;
  //       }

  //       setReviews(normalized);

  //       // If API didn't provide rating/count, compute from normalized
  //       setTotalReviewCount((prev) => {
  //         const apiCount =
  //           (raw as ApiResponseV1).totalReviewCount ??
  //           legacyWidget?.gbpLocationSummary?.reviewsCount;

  //         return apiCount ?? normalized.length ?? prev;
  //       });

  //       setAverageRating((prev) => {
  //         const apiAvg =
  //           (raw as ApiResponseV1).averageRating ??
  //           legacyWidget?.gbpLocationSummary?.rating;

  //         return apiAvg ?? computeAverage(normalized) ?? prev;
  //       });
  //     } catch (err) {
  //       console.error('Error fetching Featurable widget:', err);
  //       setError(
  //         (reviewsTexts.error as string) ??
  //           'Impossible de charger les avis pour le moment.'
  //       );
  //     } finally {
  //       setIsLoading(false);
  //     }
  //   };

  //   fetchReviews();
  // }, [isEnglish, reviewsTexts.error]);

  // autoplay
  useEffect(() => {
    try {
      setIsLoading(true);
      setError(null);

      let normalized = [...MOCK_REVIEWS];

      // Remove empty reviews
      normalized = normalized.filter(
        (r) => (r.text || r.originalText).trim().length > 0
      );

      // If EN: prefer translated text
      if (isEnglish) {
        const withEnglishText = normalized.filter(
          (r) => (r.text || '').trim().length > 0
        );
        if (withEnglishText.length) normalized = withEnglishText;
      }

      setReviews(normalized);
      setTotalReviewCount(normalized.length);
      setAverageRating(computeAverage(normalized));
      setProfileUrl(undefined); // optional
    } catch (e) {
      console.error('Error loading mock reviews:', e);
      setError(
        (reviewsTexts.error as string) ??
        'Impossible de charger les avis pour le moment.'
      );
    } finally {
      setIsLoading(false);
    }
  }, [isEnglish, reviewsTexts.error]);

  useEffect(() => {
    if (!embla || reviews.length <= 1) return;

    const id = setInterval(() => {
      try {
        embla.scrollNext();
      } catch {
        // ignore
      }
    }, 5000);

    return () => clearInterval(id);
  }, [embla, reviews.length]);

  if (!FEATURABLE_ID) return null;

  const hasRating =
    typeof averageRating === 'number' &&
    !Number.isNaN(averageRating) &&
    typeof totalReviewCount === 'number' &&
    totalReviewCount > 0;

  return (
    <section className={classes.section} aria-labelledby="reviews-title">
      <div className={classes.container}>
        <div className={classes.header}>
          {badge && (
            <div className={classes.badgeWrapper}>
              <IconSparkles size={18} className={classes.badgeIcon} />
              <span className={classes.badge}>{badge}</span>
            </div>
          )}

          <h2 id="reviews-title" className={classes.title}>
            {displayTitle}
          </h2>

          <p className={classes.description}>{displayDescription}</p>
        </div>

        {hasRating && (
          <div className={classes.ratingSummary} aria-label="Note Google globale">
            <div className={classes.ratingMain}>
              <span className={classes.ratingValue}>{averageRating!.toFixed(1)}</span>
              {renderStars(averageRating)}
            </div>

            <div className={classes.ratingMeta}>
              <span className={classes.ratingMetaStrong}>
                {formatAverageLabel(totalReviewCount!)}
              </span>

              <span className={classes.googleBadge}>
                <span className={classes.googleLogo} aria-hidden="true">
                  <span>G</span>
                </span>
                <span className={classes.googleText}>
                  {(reviewsTexts.googleBadge as string) ?? 'sur Google'}
                </span>

                {profileUrl && (
                  <a
                    className={classes.reviewLink}
                    href={profileUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {(reviewsTexts.writeReview as string) ?? 'Écrire un avis'}
                  </a>
                )}
              </span>
            </div>
          </div>
        )}

        <div
          className={classes.carouselWrapper}
          role="region"
          aria-roledescription="carousel"
          aria-label={displayTitle}
        >
          {isLoading && (
            <p className={classes.status}>
              {(reviewsTexts.loading as string) ?? 'Chargement des avis…'}
            </p>
          )}

          {error && !isLoading && <p className={classes.status}>{error}</p>}

          {!isLoading && !error && reviews.length === 0 && (
            <p className={classes.status}>
              {(reviewsTexts.empty as string) ?? 'Aucun avis disponible.'}
            </p>
          )}

          {!isLoading && !error && reviews.length > 0 && (
            <Carousel
              withIndicators={false}
              withControls
              slideSize="100%"
              slideGap="md"
              emblaOptions={{ loop: reviews.length > 1, align: "start" }}
              height="auto"
              nextControlProps={{
                'aria-label': (reviewsTexts.next as string) ?? "Afficher l'avis suivant",
              }}
              previousControlProps={{
                'aria-label': (reviewsTexts.previous as string) ?? "Afficher l'avis précédent",
              }}
              className={classes.carousel}
              getEmblaApi={setEmbla}
            >
              {reviews.map((review) => (
                <Carousel.Slide key={review.id}>
                  <article
                    className={classes.reviewCard}
                    aria-label={`${displayTitle} – ${review.authorName}`}
                  >
                    <header className={classes.reviewHeader}>
                      <div className={classes.reviewAuthor}>
                        <div className={classes.reviewAvatarWrapper}>
                          <div className={classes.reviewAvatarFallback} aria-hidden="true">
                            {getInitial(review.authorName)}
                          </div>

                          {review.avatarUrl && (
                            <img
                              className={classes.reviewAvatarImg}
                              src={review.avatarUrl}
                              alt={`Photo de profil de ${review.authorName}`}
                              loading="lazy"
                              onError={(e) => {
                                e.currentTarget.style.visibility = 'hidden';
                              }}
                            />
                          )}
                        </div>

                        <div className={classes.reviewAuthorInfo}>
                          <p className={classes.reviewAuthorName}>{review.authorName}</p>

                          <div className={classes.reviewRatingRow}>
                            {renderStars(review.ratingValue)}
                            <span className={classes.reviewRatingValue}>
                              <span className={classes.srOnly}>
                                {formatSrRating(review.ratingValue, review.ratingMax)}
                              </span>
                              {review.ratingValue.toFixed(1)} / {review.ratingMax}
                            </span>
                          </div>
                        </div>
                      </div>
                    </header>

                    <p className={classes.reviewText}>{getReviewText(review)}</p>

                    <footer className={classes.reviewFooter}>
                      <p className={classes.reviewSource}>
                        {(reviewsTexts.sourceLabel as string) ?? 'Avis Google'}
                      </p>
                    </footer>
                  </article>
                </Carousel.Slide>
              ))}
            </Carousel>
          )}
        </div>
      </div>
    </section>
  );
};
